import { FC, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Popover } from 'antd';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Crop } from 'react-image-crop';

import { updateCompanyImage, updateSessionUserImage } from '~src/store/session/actions';
import { userManagementUpdateUserImage } from '~src/modules/users/actions/user.actions';
import SvgIcon from '~src/shared/SvgIcon';
import { StoreI } from '~src/rootReducer';
import { KeyTextObj } from '~src/interfaces/translation.interface';
import { SessionUserI } from '~src/interfaces/session.types';

import ImageCropperModal from './ImageCropperModal';
import { DropZoneText, DropZoneTitle, ImageDropZoneContainer } from './imageUploader.sc';
import { CropData, ImageResponse, sendImageToBackend } from './sendImageToBackend';

interface OwnProps {
  userId?: string;
  fromWhere?: string;
  isInForm?: boolean;
  type: 'company' | 'user';
  disabled?: boolean;
  disabledMessage?: KeyTextObj;
}

interface StateProps {
  loggedInUser: SessionUserI | null;
}

interface DispatchProps {
  updateUserImage: (imageUrl: string) => Promise<void>;
  userManagementUpdateUserImage: (data: ImageResponse) => Promise<void>;
  updateCompanyImage: (imageUrl: string) => Promise<void>;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

const ImageDropZone: FC<Props> = (props) => {
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  const toggleModal = () => {
    setShowCropper(s => !s);
  };

  const showModalWithImage = (files: File[]) => {
    // prepare the image for the preview with a FileReader
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    // only interested in the first file
    reader.readAsDataURL(files[0]);
    setShowCropper(true);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop: showModalWithImage });

  const saveImageToBackend = (crops: Crop, callback: (error?: string) => void) => {
    const cropData: CropData = {
      toCropImgW: crops.width,
      toCropImgH: crops.height,
      toCropImgX: crops.x || 0,
      toCropImgY: crops.y || 0,
    };
    let saveUrl: string;
    if (props.type === 'user') {
      saveUrl = `/users/${props.userId}/image`;
    } else {
      saveUrl = '/company/image';
    }
    sendImageToBackend(imagePreview, saveUrl, cropData, response => {
      if (response !== 'error' && typeof response !== 'string') {
        if (props.type === 'user') {
          if (props.loggedInUser && props.loggedInUser.uuid === props.userId) {
            props.updateUserImage(response.data.imageUrl);
          }
          if (props.fromWhere && props.fromWhere === 'userManagement') {
            props.userManagementUpdateUserImage(response.data);
          }
        } else if (props.type === 'company') {
          props.updateCompanyImage(response.data.imageUrl);
        }
        callback();
      } else {
        callback('error');
      }
    });
  };

  const renderWithPopover = (content: JSX.Element) => (
    <Popover
      content={<div>{props.disabledMessage && props.disabledMessage.txt}</div>}
      trigger="hover"
      placement="bottom"
    >
      {content}
    </Popover>
  );

  const renderDropZone = () => (
    <DropZoneTitle {...getRootProps()}>
      <input {...getInputProps()} />
      <SvgIcon name="iconCamera" />
      <DropZoneText>{props.t('x.imageupload.dropzone.title')}</DropZoneText>
    </DropZoneTitle>
  );

  return (
    <section>
      <ImageCropperModal
        titleKey={`x.imageupload.modal.header.${props.type}`}
        show={showCropper}
        image={imagePreview}
        onHide={toggleModal}
        saveImagetoBackend={saveImageToBackend}
      />
      <ImageDropZoneContainer $isInForm={props.isInForm}>
        {props.disabled ? renderWithPopover(renderDropZone()) : renderDropZone()}
      </ImageDropZoneContainer>
    </section>
  );
};

const mapStateToProps = (state: StoreI) => ({
  loggedInUser: state.user.session.user,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  updateUserImage: imageUrl => dispatch(updateSessionUserImage(imageUrl)),
  userManagementUpdateUserImage: data => dispatch(userManagementUpdateUserImage(data)),
  updateCompanyImage: imageUrl => dispatch(updateCompanyImage(imageUrl)),
});

const ImageDropZoneT = withTranslation()(ImageDropZone);
const ImageDropZoneS = connect(mapStateToProps, mapDispatchToProps)(ImageDropZoneT);

export default ImageDropZoneS;
